import { useEffect, useState } from 'react';
import { ResponsibleRes, UserRole } from '../../services/api.types';
import { apiService } from '../../services/api';
import { Box, Tab, Tabs } from '@mui/material';
import { DataAutocomplete } from '../SelectResponsibles';
import { useAppSelector } from '../../hooks';
import { Button } from '../Button';
import { PaymentsTable } from '../PaymentsTable';
import { CreditsTable } from '../CreditsTable';

type BilletLabelProps = {
  link: string;
  disabled?: boolean;
};

export function SeeBillet({ link, disabled }: BilletLabelProps) {
  return (
    <Button
      href={link}
      target="_blank"
      circular
      color="secondary"
      className="w-min whitespace-nowrap normal-case"
      disabled={disabled}
      size="medium"
    >
      Abrir
    </Button>
  );
}

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export function FinancialTabPanel() {
  const user = useAppSelector((state) => state.user);
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const [tabValue, setTabValue] = useState<number>(0);
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(false);
  const [responsibles, setResponsibles] = useState<DataAutocomplete[]>([]);

  const handleTabsChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: number
  ) => {
    setTabValue(newValue);
  };

  const parseResponsibleToDataAutocomplete = (
    responsible: ResponsibleRes
  ): DataAutocomplete => {
    return {
      displayName: responsible.user.fullName,
      id: responsible.id,
      role: UserRole.RESPONSIBLE,
    };
  };

  useEffect(() => {
    if (isAdminOrEmployee) {
      setIsFetchingUser(true);
      apiService
        .getAllResponsibles()
        .then((rs) => {
          setResponsibles(rs.map(parseResponsibleToDataAutocomplete));
        })
        .finally(() => setIsFetchingUser(false));
    } else if (user?.role === UserRole.RESPONSIBLE) {
      setIsFetchingUser(true);
      apiService
        .getResponsible(user.id)
        .then((rs) => {
          const responsibleSelected = parseResponsibleToDataAutocomplete(rs);
          setResponsibles([responsibleSelected]);
        })
        .finally(() => setIsFetchingUser(false));
    }
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col w-full md:flex-row md:justify-between md:items-center">
        <Tabs
          value={tabValue}
          onChange={handleTabsChange}
          aria-label="basic tabs example"
          centered
          className="md:flex-1"
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          <Tab label="Pagamentos" {...a11yProps(0)} />
          <Tab label="Créditos" {...a11yProps(1)} />
        </Tabs>
      </div>
      <TabPanel value={tabValue} index={0}>
        <PaymentsTable
          isFetchingUser={isFetchingUser}
          responsibles={responsibles}
          isSelectedPanel={tabValue === 0}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <CreditsTable
          isFetchingUser={isFetchingUser}
          responsibles={responsibles}
          isSelectedPanel={tabValue === 1}
        />
      </TabPanel>
    </div>
  );
}
